// src/components/Modals/ApprovalActionModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  Divider,
  Alert,
  Chip,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CloseIcon        from '@mui/icons-material/Close';
import PendingIcon      from '@mui/icons-material/HourglassEmpty';
import DoneIcon         from '@mui/icons-material/Done';

const PENDING_PREFIXES = ['pending', 'awaiting', 'draft'];

const isPendingStatus = (status) => {
  const s = String(status || '').toLowerCase().trim();
  if (!s) return true;
  return PENDING_PREFIXES.some((p) => s.startsWith(p));
};

const hasRejection = (asset) =>
  (Array.isArray(asset?.approverList) ? asset.approverList : []).some((r) => {
    const s = String(r?.status || '').toLowerCase();
    return s.includes('rejected') || s.includes('return') || s.includes('correction');
  });

const ApprovalActionModal = ({
  open,
  asset,
  currentUser,
  approvalStatuses = [],
  approverChain    = [],
  onClose,
  onConfirm,
}) => {
  const [comment, setComment] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [statusLabel, setStatusLabel] = useState('');

  const userEmail = String(currentUser?.email || '').toLowerCase();

   const actingRow = useMemo(() => {
    if (!asset) return null;
    if (hasRejection(asset)) return null;

    const list = [...(asset.approverList || [])].sort(
      (a, b) => Number(a.level) - Number(b.level),
    );

    // Find the FIRST level that is not yet fully approved by all its users.
    // We rely on the approvedUsers array, not the text string, so custom SVL 
    // statuses like "Reviewed" or "Checked" don't break the flow.
    for (const row of list) {
      const users = Array.isArray(row.users) ? row.users : [];
      const approvedUsers = Array.isArray(row.approvedUsers) ? row.approvedUsers : [];
      
      const isLevelFullyApproved = users.length > 0 && users.every(u => approvedUsers.includes(u));
      
      // If this level isn't fully approved yet, this is the active row
      if (!isLevelFullyApproved) {
        return row;
      }
    }
    return null;
  }, [asset]);

  const nextLevel = Number(actingRow?.level) || 1;

   const isAuthorized = useMemo(() => {
    if (!actingRow) return false;
    const users = Array.isArray(actingRow.users) ? actingRow.users : [];
    const inLevel = users.some(
      (e) => String(e).trim().toLowerCase() === userEmail,
    );
    if (!inLevel) return false;
    
    // Already approved at this level → they should not re-approve.
    const alreadyApproved = (actingRow.approvedUsers || []).some(
      (e) => String(e).trim().toLowerCase() === userEmail,
    );
    return !alreadyApproved;
  }, [actingRow, userEmail]);

  // SVL status for THIS level
  const svlStatusForThisLevel = useMemo(() => {
    const row = (approvalStatuses || []).find(
      (s) => Number(s.level) === nextLevel,
    );
    return row?.status || `Level ${nextLevel} Approved`;
  }, [approvalStatuses, nextLevel]);

  // Dropdown options: SVL status + hardcoded "Return"
  const statusOptions = useMemo(
    () => [
      { label: svlStatusForThisLevel, value: svlStatusForThisLevel },
      { label: 'Return', value: 'Return' },
    ],
    [svlStatusForThisLevel],
  );

  // Who has already approved at this level?
  const approvedUsers = useMemo(() => {
    if (!actingRow) return [];
    return Array.isArray(actingRow.approvedUsers) ? actingRow.approvedUsers : [];
  }, [actingRow]);

  const allUsersAtLevel = useMemo(() => {
    if (!actingRow) return [];
    return Array.isArray(actingRow.users) ? actingRow.users : [];
  }, [actingRow]);

  const pendingUsers = useMemo(() => {
    return allUsersAtLevel.filter(
      (e) => !approvedUsers.some((a) => String(a).toLowerCase() === String(e).toLowerCase()),
    );
  }, [allUsersAtLevel, approvedUsers]);

  const isFinalLevel = useMemo(() => {
    if (!asset || !actingRow) return false;
    const list = Array.isArray(asset.approverList) ? asset.approverList : [];
    const maxLevel = list.reduce(
      (m, r) => Math.max(m, Number(r.level) || 0),
      0,
    );
    return Number(actingRow.level) === maxLevel;
  }, [asset, actingRow]);

  useEffect(() => {
    if (!open) return;
    setComment('');
    setSaving(false);
    setStatusLabel(svlStatusForThisLevel || '');
  }, [open, svlStatusForThisLevel]);

  if (!asset) return null;

  const handleConfirm = async () => {
    if (saving) return;
    if (!comment.trim()) return;
    if (!statusLabel) return;

    setSaving(true);
    try {
      const derivedAction = statusLabel === 'Return' ? 'reject' : 'approve';

      await onConfirm({
        level: nextLevel,
        action: derivedAction,
        statusLabel,
        comment: comment.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  const canSubmit =
    Boolean(comment.trim()) &&
    Boolean(statusLabel) &&
    !saving &&
    isAuthorized;

  return (
    <Dialog
      open={open}
      onClose={!saving ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Approval Action
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {asset.assetName} — Level {nextLevel}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Status info */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`You are approving at Level ${nextLevel}`}
              color="primary"
              size="small"
            />
            {isFinalLevel && (
              <Chip label="Final Level" color="success" size="small" />
            )}
          </Box>

          {/* Who's approved / pending at this level */}
          {allUsersAtLevel.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Level {nextLevel} Approvers
              </Typography>
              <List dense disablePadding>
                {allUsersAtLevel.map((email) => {
                  const isApproved = approvedUsers.some(
                    (a) => String(a).toLowerCase() === String(email).toLowerCase(),
                  );
                  return (
                    <ListItem key={email} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {isApproved ? (
                          <DoneIcon fontSize="small" color="success" />
                        ) : (
                          <PendingIcon fontSize="small" color="warning" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={email}
                        secondary={isApproved ? 'Approved' : 'Pending'}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {!isAuthorized && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              You are not an assigned approver for Level {nextLevel}.
            </Alert>
          )}

          {/* Status dropdown */}
          <FormControl fullWidth size="small" disabled={saving || !isAuthorized}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Status
            </Typography>
            <Select
              value={statusLabel}
              onChange={(e) => setStatusLabel(e.target.value)}
              displayEmpty
            >
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Comment */}
          <TextField
            label="Comment"
            placeholder="Describe why you are taking this action…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
            required
            disabled={saving || !isAuthorized}
            inputProps={{ maxLength: 500 }}
            helperText={`${comment.length}/500 — required`}
          />
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSubmit}
          disableElevation
          startIcon={<CheckCircleIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalActionModal;